import React, { useState, useEffect } from 'react'
import { Modal, Button, Form, InputGroup, Badge, Spinner, Col, Row } from 'react-bootstrap'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import Select from 'react-select'
import { toast } from 'react-toastify'
import { GET_ALL_RECIPIENTS } from '../../../gql/queries/contactsApiQueries'
import {
  ADD_CAMPAIGN_TARGETS_BY_FILTER,
  REMOVE_CAMPAIGN_TARGETS_BY_FILTER,
} from '../../../gql/mutations/campaigns'
import { GET_ALL_CARRIERS_LIST } from '../../../gql/queries/carriersQuery'
import { GET_ALL_STATES_EU_LIST } from '../../../gql/queries/statesEUQuery'
import { GET_CONTACT_TAGS } from '../../../gql/queries/tags'
import { IResRecipientsAPI, NodeContact } from '../../../types/contactsApi'
import { PhoneProviderActions } from '../../../components/telephony/PhoneProviderActions'
import { MultiSelectWithActions } from 'app/tfi/components/MultiSelectWithActions'
import { getTagContrastColor } from '../../../helpers'
import { CSVImportModal } from './CSVImportModal'
import './RecipientSelectorModal.css'

const POLICY_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'PENDING', label: 'Pending' },
]

const currentYear = new Date().getFullYear()
const lastThreeYears = [currentYear - 1, currentYear, currentYear + 1]

const normalizeContactId = (id?: string | null) => {
  if (!id) return ''
  if (/^\d+$/.test(id)) return id

  try {
    const decoded = atob(id)
    const parts = decoded.split(':')
    const lastPart = parts[parts.length - 1]
    return /^\d+$/.test(lastPart) ? lastPart : id
  } catch {
    return id
  }
}

const uniqueContactIds = (ids: string[]) => {
  const seen = new Set<string>()
  return ids.reduce<string[]>((result, id) => {
    const normalized = normalizeContactId(id)
    if (!normalized || seen.has(normalized)) return result
    seen.add(normalized)
    result.push(normalized)
    return result
  }, [])
}

interface ActiveFilters {
  state: string[]
  carrier: string[]
  policyStatus: string
  isPolicyHolder: boolean | undefined
  withoutPolicy: boolean | undefined
  effectiveYears: number[]
  tags: string[]
}

const EMPTY_FILTERS: ActiveFilters = {
  state: [],
  carrier: [],
  policyStatus: '',
  isPolicyHolder: undefined,
  withoutPolicy: undefined,
  effectiveYears: [],
  tags: [],
}

interface RecipientSelectorModalProps {
  show: boolean
  onHide: () => void
  initialSelectedIds: string[]
  onSave: (selectedContactIds: string[]) => void
  confirmLabel?: string
  campaignId?: string
  onBulkAdded?: () => void
}

export const RecipientSelectorModal: React.FC<RecipientSelectorModalProps> = ({
  show,
  onHide,
  initialSelectedIds,
  onSave,
  confirmLabel,
  campaignId,
  onBulkAdded,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds.map(normalizeContactId))
  const [highlightedAvailable, setHighlightedAvailable] = useState<string[]>([])
  const [highlightedSelected, setHighlightedSelected] = useState<string[]>([])
  const [showCSVImport, setShowCSVImport] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [pendingFilters, setPendingFilters] = useState<ActiveFilters>(EMPTY_FILTERS)
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>(EMPTY_FILTERS)
  const [knownContacts, setKnownContacts] = useState<Record<string, NodeContact>>({})
  const activeTagsValue = activeFilters.tags.length > 0 ? activeFilters.tags.join(',') : undefined
  const activeStatesValue = activeFilters.state.length > 0 ? activeFilters.state.join(',') : undefined
  const activeCarriersValue = activeFilters.carrier.length > 0 ? activeFilters.carrier.join(',') : undefined
  const activePolicyRelationship =
    activeFilters.withoutPolicy === true
      ? 'no_policy'
      : activeFilters.isPolicyHolder === true
        ? 'holder'
        : activeFilters.isPolicyHolder === false && activeFilters.withoutPolicy === false
          ? 'family'
          : ''

  const activeFilterCount = [
    activeFilters.state.length > 0 ? 'x' : '',
    activeFilters.carrier.length > 0 ? 'x' : '',
    activeFilters.policyStatus,
    activePolicyRelationship ? 'x' : '',
    activeFilters.effectiveYears.length > 0 ? 'x' : '',
    activeFilters.tags.length > 0 ? 'x' : '',
  ].filter(Boolean).length

  // Dropdown data
  const { data: carriersData } = useQuery(GET_ALL_CARRIERS_LIST, { skip: !show })
  const { data: statesData } = useQuery(GET_ALL_STATES_EU_LIST, { skip: !show })
  const { data: tagsData } = useQuery(GET_CONTACT_TAGS, { skip: !show })

  const carriers: { id: string; name: string }[] = carriersData?.allInsuranceOperatorsList ?? []
  const states: { id: string; name: string; code: string }[] = statesData?.allStatesList ?? []
  const tags: { id: string; name: string; color: string }[] = tagsData?.contactTags ?? []
  const stateOptions = states.map((state) => ({ value: state.code, label: `${state.name} (${state.code})` }))
  const carrierOptions = carriers.map((carrier) => ({ value: carrier.id, label: carrier.name }))
  const tagOptions = tags.map((tag) => ({ value: tag.id, label: tag.name, color: tag.color }))
  const selectedPendingTagOptions = tagOptions.filter((option) => pendingFilters.tags.includes(option.value))

  const [getRecipients, { data, loading, error }] = useLazyQuery<IResRecipientsAPI>(
    GET_ALL_RECIPIENTS,
    { fetchPolicy: 'network-only' }
  )

  const [getContactsById, { data: dataById }] = useLazyQuery<IResRecipientsAPI>(
    GET_ALL_RECIPIENTS,
    { fetchPolicy: 'network-only' }
  )

  const [getSelectedRecipients, { data: selectedData, loading: loadingSelected }] = useLazyQuery<IResRecipientsAPI>(
    GET_ALL_RECIPIENTS,
    { fetchPolicy: 'network-only' }
  )

  const [getAllMatchingIds, { loading: loadingRemoveAll }] = useLazyQuery<IResRecipientsAPI>(
    GET_ALL_RECIPIENTS,
    { fetchPolicy: 'network-only' }
  )

  const [addByFilter, { loading: addingByFilter }] = useMutation(ADD_CAMPAIGN_TARGETS_BY_FILTER)
  const [removeByFilter, { loading: removingByFilter }] = useMutation(REMOVE_CAMPAIGN_TARGETS_BY_FILTER)

  // Update known contacts cache
  useEffect(() => {
    if (data?.allRecipients?.edges) {
      setKnownContacts((prev) => {
        const next = { ...prev }
        data.allRecipients.edges.forEach((edge) => { next[normalizeContactId(edge.node.id)] = edge.node })
        return next
      })
    }
  }, [data])

  useEffect(() => {
    if (dataById?.allRecipients?.edges) {
      setKnownContacts((prev) => {
        const next = { ...prev }
        dataById.allRecipients.edges.forEach((edge) => { next[normalizeContactId(edge.node.id)] = edge.node })
        return next
      })
    }
  }, [dataById])

  useEffect(() => {
    if (selectedData?.allRecipients?.edges) {
      setKnownContacts((prev) => {
        const next = { ...prev }
        selectedData.allRecipients.edges.forEach((edge) => { next[normalizeContactId(edge.node.id)] = edge.node })
        return next
      })
    }
  }, [selectedData])

  // Fetch initial selected contacts if missing from cache
  useEffect(() => {
    if (show && initialSelectedIds.length > 0) {
      const normalizedInitialIds = initialSelectedIds.map(normalizeContactId)
      const missingIds = normalizedInitialIds.filter((id) => !knownContacts[id])
      if (missingIds.length > 0) {
        getContactsById({ variables: { ids: missingIds.join(','), first: missingIds.length } })
      }
    }
  }, [show, initialSelectedIds, knownContacts, getContactsById])

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Fetch contacts when search or active filters change
  useEffect(() => {
    if (show) {
      getRecipients({
        variables: {
          name: debouncedSearch || undefined,
          state: activeStatesValue,
          carrier: activeCarriersValue,
          policyStatus: activeFilters.policyStatus || undefined,
          isPolicyHolder: activeFilters.isPolicyHolder,
          withoutPolicy: activeFilters.withoutPolicy,
          effectiveYear: activeFilters.effectiveYears.length > 0 ? activeFilters.effectiveYears.join(',') : undefined,
          tags: activeTagsValue,
          first: 100,
          offset: 0,
        },
      })
    }
  }, [debouncedSearch, activeFilters, show, getRecipients])

  useEffect(() => {
    if (!show || selectedIds.length === 0) return

    const hasSearchOrFilters = !!(
      debouncedSearch ||
      activeFilters.state.length > 0 ||
      activeFilters.carrier.length > 0 ||
      activeFilters.policyStatus ||
      activeFilters.isPolicyHolder !== undefined ||
      activeFilters.withoutPolicy !== undefined ||
      activeFilters.effectiveYears.length > 0 ||
      activeFilters.tags.length > 0
    )

    if (!hasSearchOrFilters) return

    getSelectedRecipients({
      variables: {
        ids: selectedIds.join(','),
        name: debouncedSearch || undefined,
        state: activeStatesValue,
        carrier: activeCarriersValue,
        policyStatus: activeFilters.policyStatus || undefined,
        isPolicyHolder: activeFilters.isPolicyHolder,
        withoutPolicy: activeFilters.withoutPolicy,
        effectiveYear: activeFilters.effectiveYears.length > 0 ? activeFilters.effectiveYears.join(',') : undefined,
        tags: activeTagsValue,
        first: selectedIds.length,
        offset: 0,
      },
    })
  }, [selectedIds, debouncedSearch, activeFilters, show, getSelectedRecipients])

  // Reset on open
  useEffect(() => {
    if (show) {
      setSelectedIds(initialSelectedIds.map(normalizeContactId))
      setHighlightedAvailable([])
      setHighlightedSelected([])
      setSearchTerm('')
      setShowCSVImport(false)
      setShowFilters(false)
      setPendingFilters(EMPTY_FILTERS)
      setActiveFilters(EMPTY_FILTERS)
    }
  }, [show, initialSelectedIds])

  const allRecipients = data?.allRecipients?.edges ?? []
  const totalCount: number = (data?.allRecipients as any)?.totalCount ?? 0
  const selectedIdSet = new Set(selectedIds)
  const availableContacts = allRecipients.filter((edge) => !selectedIdSet.has(normalizeContactId(edge.node.id)))
  const hasSearchOrFilters = !!(
    debouncedSearch ||
    activeFilters.state.length > 0 ||
    activeFilters.carrier.length > 0 ||
    activeFilters.policyStatus ||
    activeFilters.isPolicyHolder !== undefined ||
    activeFilters.withoutPolicy !== undefined ||
    activeFilters.effectiveYears.length > 0 ||
    activeFilters.tags.length > 0
  )
  const matchingSelectedIds = hasSearchOrFilters
    ? new Set((selectedData?.allRecipients?.edges ?? []).map((edge) => normalizeContactId(edge.node.id)))
    : null
  const visibleSelectedIds = hasSearchOrFilters
    ? selectedIds.filter((id) => matchingSelectedIds?.has(id))
    : selectedIds
  const selectedContacts = visibleSelectedIds.map((id) => knownContacts[id]).filter(Boolean)

  // Move handlers
  const moveToSelected = () => {
    setSelectedIds((prev) => uniqueContactIds([...prev, ...highlightedAvailable]))
    setHighlightedAvailable([])
  }
  const moveToAvailable = () => {
    setSelectedIds(selectedIds.filter((id) => !highlightedSelected.includes(id)))
    setHighlightedSelected([])
  }
  const moveAllToSelected = () => {
    const allIds = availableContacts.map((edge) => normalizeContactId(edge.node.id))
    setSelectedIds((prev) => uniqueContactIds([...prev, ...allIds]))
  }
  const moveAllToAvailable = () => {
    setSelectedIds([])
    setHighlightedSelected([])
  }

  const toggleAvailableHighlight = (id: string) => {
    setHighlightedAvailable((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }
  const toggleSelectedHighlight = (id: string) => {
    setHighlightedSelected((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  const handleApplyFilters = () => {
    setActiveFilters({ ...pendingFilters })
    setShowFilters(false)
  }

  const handleClearFilters = () => {
    setPendingFilters(EMPTY_FILTERS)
    setActiveFilters(EMPTY_FILTERS)
  }

  const handleAddAllMatching = async () => {
    if (!campaignId) return
    try {
      const { data: result } = await addByFilter({
        variables: {
          campaignId,
          name: debouncedSearch || undefined,
          state: activeStatesValue,
          carrier: activeCarriersValue,
          policyStatus: activeFilters.policyStatus || undefined,
          effectiveYear: activeFilters.effectiveYears.length > 0 ? activeFilters.effectiveYears.join(',') : undefined,
          tags: activeTagsValue,
          isPolicyHolder: activeFilters.isPolicyHolder,
          withoutPolicy: activeFilters.withoutPolicy,
        },
      })
      const added = result?.addCampaignTargetsByFilter?.addedCount ?? 0
      const skipped = result?.addCampaignTargetsByFilter?.skippedCount ?? 0
      toast.success(
        `${added} contact${added !== 1 ? 's' : ''} added as targets${skipped > 0 ? ` (${skipped} already existed)` : ''}`
      )
      onBulkAdded?.()
      onHide()
    } catch {
      toast.error('Could not add all matching contacts')
    }
  }

  const handleRemoveAllMatching = async () => {
    if (campaignId) {
      try {
        const { data: result } = await removeByFilter({
          variables: {
            campaignId,
            name: debouncedSearch || undefined,
            state: activeStatesValue,
            carrier: activeCarriersValue,
            policyStatus: activeFilters.policyStatus || undefined,
            effectiveYear: activeFilters.effectiveYears.length > 0 ? activeFilters.effectiveYears.join(',') : undefined,
            tags: activeTagsValue,
            isPolicyHolder: activeFilters.isPolicyHolder,
            withoutPolicy: activeFilters.withoutPolicy,
          },
        })
        const removed = result?.removeCampaignTargetsByFilter?.removedCount ?? 0
        toast.success(`${removed} target${removed !== 1 ? 's' : ''} removed`)
        onBulkAdded?.()
        onHide()
        return
      } catch {
        toast.error('Could not remove matching contacts')
        return
      }
    }

    const hasSearch = !!debouncedSearch
    const hasFilters = !!(
      activeFilters.state.length > 0 ||
      activeFilters.carrier.length > 0 ||
      activeFilters.policyStatus ||
      activeFilters.isPolicyHolder !== undefined ||
      activeFilters.withoutPolicy !== undefined ||
      activeFilters.effectiveYears.length > 0 ||
      activeFilters.tags.length > 0
    )

    if (!hasSearch && !hasFilters) {
      setSelectedIds([])
      setHighlightedSelected([])
      return
    }

    const PAGE_SIZE = 100
    let offset = 0
    const allMatchingIds: string[] = []

    while (true) {
      const { data: result } = await getAllMatchingIds({
        variables: {
          name: debouncedSearch || undefined,
          state: activeStatesValue,
          carrier: activeCarriersValue,
          policyStatus: activeFilters.policyStatus || undefined,
          isPolicyHolder: activeFilters.isPolicyHolder,
          withoutPolicy: activeFilters.withoutPolicy,
          effectiveYear: activeFilters.effectiveYears.length > 0 ? activeFilters.effectiveYears.join(',') : undefined,
          tags: activeTagsValue,
          first: PAGE_SIZE,
          offset,
        },
      })
      const edges = result?.allRecipients?.edges ?? []
      edges.forEach((e: any) => allMatchingIds.push(normalizeContactId(e.node.id)))
      if (edges.length < PAGE_SIZE) break
      offset += PAGE_SIZE
    }

    const matchingIds = new Set(allMatchingIds)
    setSelectedIds((prev) => prev.filter((id) => !matchingIds.has(id)))
    setHighlightedSelected([])
  }

  const handleSave = () => onSave(selectedIds)

  const handleImportSuccess = (importedContactIds: string[]) => {
    setSelectedIds((prev) => uniqueContactIds([...prev, ...importedContactIds]))
    getRecipients({ variables: { name: debouncedSearch || undefined, first: 100, offset: 0 } })
  }

  const renderContactItem = (
    contact: NodeContact,
    isHighlighted: boolean,
    onToggle: () => void
  ) => (
    <div
      key={contact.id}
      className={`recipient-item ${isHighlighted ? 'highlighted' : ''}`}
      onClick={onToggle}
    >
      <div className='form-check'>
        <input
          className='form-check-input'
          type='checkbox'
          checked={isHighlighted}
          onChange={onToggle}
          onClick={(e) => e.stopPropagation()}
        />
        <div className='recipient-info'>
          <div className='fw-bold'>
            {contact.fullName || `${contact.firstName} ${contact.lastName}`}
          </div>
          <div className='text-muted small'>
            {contact.email && <span className='me-3'>{contact.email}</span>}
            {contact.phone && (
              <PhoneProviderActions
                phone={contact.phone}
                stopPropagation={false}
                className='d-inline-flex flex-column align-items-start'
                textClassName='text-muted small'
              />
            )}
          </div>
          {contact.tagAssignments && contact.tagAssignments.length > 0 && (
            <div className='d-flex flex-wrap gap-1 mt-1'>
              {contact.tagAssignments.map((a: any) => (
                <span
                  key={a.tag.id}
                  className='badge rounded-pill'
                  style={{
                    backgroundColor: a.tag.color,
                    color: getTagContrastColor(a.tag.color),
                    fontSize: '0.65rem',
                  }}
                >
                  {a.tag.name}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <Modal show={show} onHide={onHide} size='xl' centered>
      <Modal.Header closeButton>
        <Modal.Title>Select Campaign Recipients</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Filters panel */}
        {showFilters && (
          <div className='border rounded p-4 mb-4 bg-light'>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <span className='fw-semibold'>Filters</span>
              <Button variant='link' size='sm' className='p-0 text-muted' onClick={() => setShowFilters(false)}>
                <i className='bi bi-x-lg'></i>
              </Button>
            </div>
            <Row className='g-3'>
              <Col md={3}>
                <Form.Label className='small fw-semibold'>State</Form.Label>
                <MultiSelectWithActions
                  value={stateOptions.filter((option) => pendingFilters.state.includes(option.value))}
                  options={stateOptions}
                  onChange={(selected: any) =>
                    setPendingFilters((f) => ({
                      ...f,
                      state: selected ? selected.map((option: any) => option.value) : [],
                    }))
                  }
                />
              </Col>
              <Col md={3}>
                <Form.Label className='small fw-semibold'>Carrier</Form.Label>
                <MultiSelectWithActions
                  value={carrierOptions.filter((option) => pendingFilters.carrier.includes(String(option.value)))}
                  options={carrierOptions}
                  onChange={(selected: any) =>
                    setPendingFilters((f) => ({
                      ...f,
                      carrier: selected ? selected.map((option: any) => option.value) : [],
                    }))
                  }
                />
              </Col>
              <Col md={3}>
                <Form.Label className='small fw-semibold'>Policy status</Form.Label>
                <Form.Select
                  size='sm'
                  value={pendingFilters.policyStatus}
                  onChange={(e) => setPendingFilters((f) => ({ ...f, policyStatus: e.target.value }))}
                >
                  <option value=''>Any status</option>
                  {POLICY_STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className='small fw-semibold'>Policy relationship</Form.Label>
                <Form.Select
                  size='sm'
                  value={
                    pendingFilters.withoutPolicy === true
                      ? 'no_policy'
                      : pendingFilters.isPolicyHolder === true
                        ? 'holder'
                        : pendingFilters.isPolicyHolder === false && pendingFilters.withoutPolicy === false
                          ? 'family'
                        : ''
                  }
                  onChange={(e) =>
                    setPendingFilters((f) => ({
                      ...f,
                      isPolicyHolder:
                        e.target.value === 'holder' ? true : e.target.value === 'family' ? false : undefined,
                      withoutPolicy:
                        e.target.value === 'no_policy' ? true : e.target.value === 'holder' || e.target.value === 'family' ? false : undefined,
                    }))
                  }
                >
                  <option value=''>All contacts</option>
                  <option value='holder'>Policy holders</option>
                  <option value='family'>Family members</option>
                  <option value='no_policy'>No policy</option>
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label className='small fw-semibold'>Effective year</Form.Label>
                <div className='d-flex gap-3 mt-1'>
                  {lastThreeYears.map((year) => (
                    <Form.Check
                      key={year}
                      inline
                      label={year}
                      checked={pendingFilters.effectiveYears.includes(year)}
                      onChange={() =>
                        setPendingFilters((f) => ({
                          ...f,
                          effectiveYears: f.effectiveYears.includes(year)
                            ? f.effectiveYears.filter((y) => y !== year)
                            : [...f.effectiveYears, year],
                        }))
                      }
                    />
                  ))}
                </div>
              </Col>
              <Col md={6}>
                <Form.Label className='small fw-semibold'>Tags</Form.Label>
                <Select
                  value={selectedPendingTagOptions}
                  options={tagOptions}
                  onChange={(selected: any) =>
                    setPendingFilters((f) => ({
                      ...f,
                      tags: selected ? selected.map((option: any) => option.value) : [],
                    }))
                  }
                  isMulti
                  closeMenuOnSelect={false}
                  placeholder='Filter by tags...'
                  formatOptionLabel={(option: any) => (
                    <span className='d-flex align-items-center gap-2'>
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: option.color,
                          display: 'inline-block',
                          flexShrink: 0,
                        }}
                      />
                      {option.label}
                    </span>
                  )}
                />
              </Col>
            </Row>
            <div className='d-flex gap-2 mt-3'>
              <Button size='sm' variant='primary' onClick={handleApplyFilters}>
                Apply filters
              </Button>
              <Button size='sm' variant='light' onClick={handleClearFilters}>
                Clear
              </Button>
            </div>
          </div>
        )}

        <div className='selection-toolbar mb-3'>
          <div className='selection-toolbar-main'>
            <InputGroup className='selection-search'>
              <Form.Control
                type='text'
                placeholder='Search contacts…'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {(loading || loadingSelected) && (
                <InputGroup.Text>
                  <Spinner animation='border' size='sm' />
                </InputGroup.Text>
              )}
            </InputGroup>
            <div className='selection-toolbar-actions'>
              <Button
                size='sm'
                variant={activeFilterCount > 0 ? 'secondary' : 'light'}
                onClick={() => { setShowFilters((v) => !v); setPendingFilters({ ...activeFilters }) }}
              >
                <i className='bi bi-funnel me-1'></i>
                Filters
                {activeFilterCount > 0 && (
                  <Badge bg='light' text='primary' pill className='ms-1'>{activeFilterCount}</Badge>
                )}
              </Button>
              <Button
                variant='outline-success'
                size='sm'
                onClick={() => setShowCSVImport(true)}
                title='Import contacts from CSV'
              >
                <i className='bi bi-file-earmark-arrow-up me-1'></i>
                CSV
              </Button>
            </div>
          </div>
          {activeFilterCount > 0 && (
            <div className='d-flex flex-wrap gap-1 mt-2'>
              {activeFilters.state.map((stateCode) => (
                <span key={stateCode} className='badge fw-normal d-inline-flex align-items-center gap-1' style={{backgroundColor: '#0d6efd', color: '#fff'}}>
                  State: {states.find((state) => state.code === stateCode)?.name ?? stateCode}
                  <i className='bi bi-x' style={{ cursor: 'pointer' }} onClick={() => setActiveFilters((f) => ({ ...f, state: f.state.filter((value) => value !== stateCode) }))}></i>
                </span>
              ))}
              {activeFilters.carrier.map((carrierId) => (
                <span key={carrierId} className='badge fw-normal d-inline-flex align-items-center gap-1' style={{backgroundColor: '#0d6efd', color: '#fff'}}>
                  Carrier: {carriers.find((c) => c.id === carrierId)?.name ?? carrierId}
                  <i className='bi bi-x' style={{ cursor: 'pointer' }} onClick={() => setActiveFilters((f) => ({ ...f, carrier: f.carrier.filter((value) => value !== carrierId) }))}></i>
                </span>
              ))}
              {activeFilters.policyStatus && (
                <span className='badge fw-normal d-inline-flex align-items-center gap-1' style={{backgroundColor: '#0d6efd', color: '#fff'}}>
                  Status: {activeFilters.policyStatus}
                  <i className='bi bi-x' style={{ cursor: 'pointer' }} onClick={() => setActiveFilters((f) => ({ ...f, policyStatus: '' }))}></i>
                </span>
              )}
              {(activeFilters.isPolicyHolder !== undefined || activeFilters.withoutPolicy !== undefined) && (
                <span className='badge fw-normal d-inline-flex align-items-center gap-1' style={{backgroundColor: '#0d6efd', color: '#fff'}}>
                  {activePolicyRelationship === 'no_policy'
                    ? 'No policy'
                    : activePolicyRelationship === 'holder'
                      ? 'Policy holder'
                      : 'Family member'}
                  <i className='bi bi-x' style={{ cursor: 'pointer' }} onClick={() => setActiveFilters((f) => ({ ...f, isPolicyHolder: undefined, withoutPolicy: undefined }))}></i>
                </span>
              )}
              {activeFilters.effectiveYears.length > 0 && (
                <span className='badge fw-normal d-inline-flex align-items-center gap-1' style={{backgroundColor: '#6f42c1', color: '#fff'}}>
                  Year: {activeFilters.effectiveYears.join(', ')}
                  <i className='bi bi-x' style={{ cursor: 'pointer' }} onClick={() => setActiveFilters((f) => ({ ...f, effectiveYears: [] }))}></i>
                </span>
              )}
              {activeFilters.tags.map((tagId) => {
                const tag = tags.find((t) => t.id === tagId)
                const bg = tag?.color ?? '#6c757d'
                return (
                  <span
                    key={tagId}
                    className='badge fw-normal d-inline-flex align-items-center gap-1'
                    style={{backgroundColor: bg, color: getTagContrastColor(bg)}}
                  >
                    {tag?.name ?? tagId}
                    <i
                      className='bi bi-x'
                      style={{ cursor: 'pointer' }}
                      onClick={() => setActiveFilters((f) => ({ ...f, tags: f.tags.filter((id) => id !== tagId) }))}
                    ></i>
                  </span>
                )
              })}
            </div>
          )}
        </div>

        <div className='recipient-selector-container'>
          {/* Available Column (left) */}
          <div className='recipient-column'>
            <div className='column-header'>
              <h6 className='mb-0'>
                Available Contacts
                {totalCount > 0 && (
                  <Badge bg='secondary' pill className='ms-2'>{totalCount}</Badge>
                )}
              </h6>
            </div>
            <div className='contacts-list'>
              {error ? (
                <div className='text-center text-danger p-4 small'>
                  <i className='bi bi-exclamation-triangle me-2'></i>
                  {error.message}
                </div>
              ) : loading && availableContacts.length === 0 ? (
                <div className='text-center p-4'>
                  <Spinner animation='border' size='sm' />
                </div>
              ) : availableContacts.length === 0 ? (
                <div className='text-center text-muted p-4'>
                  <i className='bi bi-inbox fs-1 d-block mb-2'></i>
                  No contacts found
                </div>
              ) : (
                availableContacts.map(({ node: contact }) =>
                  renderContactItem(
                    contact,
                    highlightedAvailable.includes(normalizeContactId(contact.id)),
                    () => toggleAvailableHighlight(normalizeContactId(contact.id))
                  )
                )
              )}
            </div>
          </div>

          {/* Control Buttons */}
          <div className='control-buttons'>
            <Button
              variant='outline-primary'
              size='sm'
              onClick={moveToSelected}
              disabled={highlightedAvailable.length === 0}
              title='Add selected'
            >
              <i className='bi bi-chevron-right'></i>
            </Button>
            <Button
              variant='outline-primary'
              size='sm'
              onClick={moveAllToSelected}
              disabled={availableContacts.length === 0}
              title='Add all visible'
            >
              <i className='bi bi-chevron-double-right'></i>
            </Button>
            <Button
              variant='outline-secondary'
              size='sm'
              onClick={moveToAvailable}
              disabled={highlightedSelected.length === 0}
              title='Remove selected'
            >
              <i className='bi bi-chevron-left'></i>
            </Button>
            <Button
              variant='outline-secondary'
              size='sm'
              onClick={moveAllToAvailable}
              disabled={selectedIds.length === 0}
              title='Remove all'
            >
              <i className='bi bi-chevron-double-left'></i>
            </Button>
          </div>

          {/* Selected Column (right) */}
          <div className='recipient-column'>
            <div className='column-header'>
              <h6 className='mb-3'>
                Selected Recipients
                {selectedIds.length > 0 && (
                  <Badge bg='success' pill className='ms-2'>{selectedIds.length}</Badge>
                )}
              </h6>
              <div style={{ minHeight: '38px' }}>
                {selectedIds.length > 0 && (
                  <div className='alert alert-success mb-0 py-1 small'>
                    <i className='bi bi-check-circle me-2'></i>
                    {selectedIds.length} contact{selectedIds.length !== 1 && 's'} selected
                    {hasSearchOrFilters && (
                      <span className='ms-2 text-muted'>
                        ({visibleSelectedIds.length} matching current filters)
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className='contacts-list'>
              {loadingSelected && hasSearchOrFilters && selectedContacts.length === 0 ? (
                <div className='text-center p-4'>
                  <Spinner animation='border' size='sm' />
                </div>
              ) : selectedContacts.length === 0 ? (
                <div className='text-center text-muted p-4'>
                  <i className='bi bi-person-plus fs-1 d-block mb-2'></i>
                  {selectedIds.length > 0 ? (
                    <span>
                      {hasSearchOrFilters ? 'No selected recipients match current filters' : `${selectedIds.length} recipients selected`}
                      <br />
                      <small className='text-muted'>
                        {hasSearchOrFilters ? 'Adjust search or filters to see them' : '(Details loading)'}
                      </small>
                    </span>
                  ) : (
                    <span>
                      No recipients selected yet
                      <br />
                      <small>Select contacts from the left</small>
                    </span>
                  )}
                </div>
              ) : (
                selectedContacts.map((contact) =>
                  renderContactItem(
                    contact,
                    highlightedSelected.includes(normalizeContactId(contact.id)),
                    () => toggleSelectedHighlight(normalizeContactId(contact.id))
                  )
                )
              )}
            </div>
          </div>

        </div>
      </Modal.Body>

      <Modal.Footer className='justify-content-between'>
        {/* Bulk actions */}
        <div className='d-flex gap-2'>
          {campaignId && (
            <Button
              variant='outline-primary'
              onClick={handleAddAllMatching}
              disabled={addingByFilter || totalCount === 0}
              title='Add all contacts matching current search and filters directly to the campaign'
            >
              {addingByFilter ? <Spinner animation='border' size='sm' className='me-2' /> : <i className='bi bi-people-fill me-2'></i>}
              Add all matching
              {totalCount > 0 && <Badge bg='primary' pill className='ms-2'>{totalCount}</Badge>}
            </Button>
          )}
          <Button
            variant='outline-danger'
            onClick={handleRemoveAllMatching}
            disabled={(loadingRemoveAll || removingByFilter) || selectedIds.length === 0}
            title='Remove from selection all contacts matching current search and filters'
          >
            {(loadingRemoveAll || removingByFilter) ? <Spinner animation='border' size='sm' className='me-2' /> : <i className='bi bi-person-dash-fill me-2'></i>}
            Remove all matching
          </Button>
        </div>

        <div className='d-flex gap-2 align-items-center'>
          {selectedIds.length > 0 && (
            <span className='text-muted small me-2'>
              <i className='bi bi-check-circle-fill text-success me-1'></i>
              {selectedIds.length} contact{selectedIds.length !== 1 ? 's' : ''} selected
            </span>
          )}
          <Button variant='secondary' onClick={onHide}>Cancel</Button>
          <Button
            variant='success'
            size='lg'
            onClick={handleSave}
            disabled={selectedIds.length === 0}
          >
            <i className='bi bi-check-lg me-2'></i>
            {confirmLabel || `Save Selection (${selectedIds.length})`}
          </Button>
        </div>
      </Modal.Footer>

      <CSVImportModal
        show={showCSVImport}
        onHide={() => setShowCSVImport(false)}
        onImportSuccess={handleImportSuccess}
      />
    </Modal>
  )
}
